/** tm/legal/LegalTopicParts.tsx — 주제 편집 조각: 출처 선택 · 관련 조문 핀 · 세부 주제(이름+패턴) */
import { C, T, R, IMEText } from '../components/ui';
import { chip, inputStyle, labelStyle, btn, groupColor, MUTED } from './legalTheme';
import type { LegalSource, PinInput, Subtopic } from './legalTypes';

export function SourcesPicker({ sources, value, onChange }: { sources: LegalSource[]; value: string[] | null; onChange: (v: string[] | null) => void }) {
  const list = sources.filter(s => s.key !== 'law');
  const all = value === null;
  const toggle = (k: string) => {
    const cur = value ?? list.filter(s => s.enabled).map(s => s.key);
    const next = cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k];
    onChange(next.length ? next : null);
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <span style={labelStyle}>출처 <span style={{ fontWeight: 400, color: MUTED }}>{all ? '기본 출처 전체에서 찾습니다' : `${value?.length}곳에서만 찾습니다`}</span></span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        <button type="button" className="lw-f" aria-pressed={all} onClick={() => onChange(null)} style={chip(all)}>기본 전체</button>
        {list.map(s => {
          const on = !all && !!value?.includes(s.key);
          const g = groupColor(s.group);
          return (
            <button key={s.key} type="button" className="lw-f" aria-pressed={on} onClick={() => toggle(s.key)} style={{ ...chip(on), display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <i aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 2, background: g.fg, display: 'inline-block' }} />{s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const rowBox = { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 } as const;
const xBtn = { ...btn(), padding: '4px 9px', color: MUTED } as const;

export function PinsEditor({ value, onChange }: { value: PinInput[]; onChange: (v: PinInput[]) => void }) {
  const set = (i: number, p: Partial<PinInput>) => onChange(value.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <div style={{ marginBottom: 12 }}>
      <span style={labelStyle}>관련 조문 (핀) <span style={{ fontWeight: 400, color: MUTED }}>법령명은 정확히, 조는 비우면 법령 전문</span></span>
      {value.map((p, i) => (
        <div key={i} style={{ ...rowBox, flexWrap: 'wrap' }}>
          <IMEText value={p.law} onChange={v => set(i, { law: v })} placeholder="조세특례제한법" aria-label="법령명" style={{ ...inputStyle, flex: '2 1 180px' }} />
          <IMEText value={p.jo} onChange={v => set(i, { jo: v })} placeholder="조 (예: 제6조)" aria-label="조" style={{ ...inputStyle, flex: '0 1 90px' }} />
          <IMEText value={p.title} onChange={v => set(i, { title: v })} placeholder="조 제목" aria-label="조 제목" style={{ ...inputStyle, flex: '2 1 160px' }} />
          <button type="button" className="lw-f" aria-label="핀 삭제" onClick={() => onChange(value.filter((_, j) => j !== i))} style={xBtn}>×</button>
        </div>
      ))}
      <button type="button" className="lw-f" onClick={() => onChange([...value, { law: '', jo: '', title: '' }])} style={{ ...btn(), padding: '4px 10px', fontSize: T.sm }}>+ 조문 추가</button>
    </div>
  );
}

export function patternError(p: string): string | null {
  if (!p.trim()) return '패턴을 입력해 주세요';
  try { new RegExp(p); return null; } catch { return '패턴 형식이 올바르지 않습니다 (예: 승계|전환)'; }
}

export function SubtopicsEditor({ value, onChange }: { value: Subtopic[]; onChange: (v: Subtopic[]) => void }) {
  const set = (i: number, p: Partial<Subtopic>) => onChange(value.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <div style={{ marginBottom: 12 }}>
      <span style={labelStyle}>세부 주제 <span style={{ fontWeight: 400, color: MUTED }}>제목·요지에 패턴이 있으면 묶습니다. 여러 말은 | 로 (예: 승계|전환)</span></span>
      {value.map((s, i) => {
        const e = patternError(s.pattern);
        return (
          <div key={i} style={{ marginBottom: 6 }}>
            <div style={rowBox}>
              <IMEText value={s.label} onChange={v => set(i, { label: v })} placeholder="이름 (예: 청년창업)" aria-label="세부 주제 이름" style={{ ...inputStyle, flex: '1 1 140px' }} />
              <IMEText value={s.pattern} onChange={v => set(i, { pattern: v })} placeholder="패턴 (예: 청년)" aria-label="패턴"
                style={{ ...inputStyle, flex: '2 1 180px', fontFamily: 'ui-monospace,Menlo,monospace', border: `1px solid ${e && s.pattern ? C.red : C.border}` }} />
              <button type="button" className="lw-f" aria-label="세부 주제 삭제" onClick={() => onChange(value.filter((_, j) => j !== i))} style={xBtn}>×</button>
            </div>
            {e && s.pattern && <div style={{ fontSize: T.sm, color: C.red }}>{e}</div>}
          </div>
        );
      })}
      <button type="button" className="lw-f" onClick={() => onChange([...value, { label: '', pattern: '' }])} style={{ ...btn(), padding: '4px 10px', fontSize: T.sm, borderRadius: R.sm }}>+ 세부 주제 추가</button>
    </div>
  );
}
